/**
 * Glass floating panel system. Base class for contextual property panels
 * that appear near clicked elements on the canvas. Handles:
 * - Positioning near anchor element (right/left/below fallback)
 * - Scroll-sync via glass overlay translateY
 * - Click-outside dismiss
 * - Escape dismiss
 * - Continuous repositioning via element-rect-update
 * - Coexistence arbitration via glass:element-claimed event
 *
 * Panel subclasses implement renderContent() and onPropertyChange().
 *
 * ── `theme:override-changed` event contract ────────────────────────────────
 * Emitted whenever an override write lands (element field, section field, or a
 * global style). Payload (all fields optional for backward compatibility):
 *   { scope: 'element' | 'section', instanceId, eid?, field? }
 * Consumers MUST treat a missing/partial payload as "refresh everything" — many
 * historical emitters (shared-spacing, resize, global button styles) still fire
 * it bare, and that must keep triggering a full re-sync. The Phase 4 panel
 * refresh keys off `{ instanceId, eid, field }` to skip unrelated panels.
 */

import { eventBus } from '../core/events.js';
import { reportError } from '../core/report-error.js';
import { bridge } from './bridge.js';
import { store } from '../core/state.js';
import {
  RESPONSIVE_VIEWPORTS,
  applyElementOverrides,
  resolveSectionByInstance
} from './section-utils.js';
import {
  deleteEditableFromHTML,
  injectEditableAttrs,
  pruneOrphanOverrides,
  stampEditableIds
} from './editable-utils.js';
import { mountSharedSpacingControls } from './glass-shared-spacing.js';
import { mountElementEntranceRow, refreshElementEntranceRow } from './glass-panel-entrance-row.js';
import { escapeHTML } from '../shared/html-escape.js';
import {
  isPhoneViewport,
  getKeyboardInset,
  getViewportHeight,
  occludedLeftPx,
  occludedRightPx,
  onViewportChange
} from '../shared/viewport-service.js';
import { icon } from '../ui/icons.js';
import {
  attachSheetDrag,
  computeSheetDetents,
  createSheetGrabber,
  detentOffsets
} from '../ui/sheet.js';
import { isPickModeActive } from './ai-context-picker.js';
import { elementSelectionSize } from './element-selection.js';
import { glassAutoOpenEnabled, shouldAutoOpenPanel } from './glass-autoopen-policy.js';
import { placeClearOf, placeVisible } from './glass-panel-placement.js';
import { panelKeyFor } from '../ui/command-bar/editable-capabilities.js';
import { isDomTarget, targetRefKey } from '../shared/target-ref.js';

// Lazy-loaded to avoid circular import (GlassPanelSection extends GlassPanel from this file)
let _GlassPanelSection = null;
async function _getSectionPanel() {
  if (!_GlassPanelSection) {
    const mod = await import('./glass-panel-section.js');
    _GlassPanelSection = mod.GlassPanelSection;
  }
  return _GlassPanelSection;
}

// ── Panel Registry ──

const _registry = new Map(); // type → PanelClass
const _activePanels = []; // currently open panels
let _claimedClick = null; // { instanceId, eid } of the current claimed click
let _lastElementRect = null; // latest rect from bridge
let _pendingOpen = null;
let _openToken = 0;
let _panelConstructionCount = 0;
let _snapshotRequestCount = 0;

/** Register a panel class for an editable type. */
export function registerPanel(editableType, PanelClass) {
  _registry.set(editableType, PanelClass);
}

/** Check if the current click was claimed by a floating panel. */
export function isElementClaimed(instanceId, eid) {
  return _claimedClick && _claimedClick.instanceId === instanceId && _claimedClick.eid === eid;
}

/** Check if the panel system is active and has a panel for this editable type. */
export function hasPanelFor(target) {
  const panelKey = typeof target === 'string' ? target : panelKeyFor(target || {});
  return _overlay !== null && !!panelKey && _registry.has(panelKey);
}

export function glassPanelMetrics() {
  return {
    constructions: _panelConstructionCount,
    snapshotRequests: _snapshotRequestCount,
    pending: !!_pendingOpen
  };
}

function _cancelPendingOpen() {
  if (_pendingOpen?.timeoutId) clearTimeout(_pendingOpen.timeoutId);
  _pendingOpen = null;
  _openToken += 1;
}

/** Check whether any glass panel is currently open. */
export function hasOpenPanels() {
  return _activePanels.length > 0;
}

// ── Panel Manager ──

let _overlay = null;
let _listeners = [];
let _docListenersWired = false;
let _offViewport = null;
let _showToastFn = null;
let _showConfirmModalFn = null;
let _showUndoToastFn = null;

/**
 * Panel width, read from the register rather than duplicated (Mobile P7).
 *
 * This was `380` here AND `width: 380px` in glass-panels.css — two copies of
 * one number, and the CSS copy is the one a media query can move. Reading the
 * token means the coarse register (or any future phone override) can change
 * the width and this positioning math follows, instead of clamping panels to a
 * stale 380 while CSS renders something else.
 *
 * Not cached: the value can change under a breakpoint crossing, and this runs
 * once per panel placement, not per frame.
 */
function panelWidthPx() {
  const fallback = 380;
  if (typeof getComputedStyle !== 'function' || typeof document === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--pb-panel-width-glass')
    .trim();
  const px = parseFloat(raw);
  return Number.isFinite(px) && px > 0 ? px : fallback;
}
const PANEL_INSET_PX = 8;
const GLASS_SHEET_DETENT_NAMES = ['full', 'half', 'peek'];

/**
 * The slice of the overlay a panel may actually occupy, in overlay
 * coordinates — or null when the whole overlay is usable.
 *
 * Two independent ways the overlay stops being fully visible, and both return
 * the same shape so every caller clamps identically:
 *
 *   1. Phone widths — the overlay is sized to the iframe, which can be wider
 *      than the screen (tablet preset). The visible slice is the scrollable
 *      wrapper's.
 *   2. Any width — a docked panel (the agent) floats over the right of the
 *      canvas at a z-index above every panel. This used to return null on
 *      desktop, so panels were placed into the covered strip and drawn
 *      underneath it.
 */
function _visibleBounds(overlayEl) {
  if (!overlayEl) return null;
  const overlayRect = overlayEl.getBoundingClientRect();
  if (!overlayRect.width) return null;

  if (isPhoneViewport()) {
    const wrapRect = document.querySelector('.pb-iframe-wrap')?.getBoundingClientRect();
    if (wrapRect?.width) {
      return { left: wrapRect.left - overlayRect.left, width: wrapRect.width };
    }
  }

  const occluded = occludedRightPx(overlayRect);
  const occludedLeft = occludedLeftPx(overlayRect);
  if (occluded > 0 || occludedLeft > 0) {
    return {
      left: occludedLeft,
      width: Math.max(0, overlayRect.width - occludedLeft - occluded)
    };
  }
  return null;
}

function _clampLeftToBounds(left, width, bounds, inset) {
  const min = bounds.left + inset;
  const max = Math.max(min, bounds.left + bounds.width - width - inset);
  return Math.min(Math.max(min, left), max);
}

/**
 * The height a panel may actually occupy.
 *
 * The overlay is `position:absolute; inset:0` inside the scrollable canvas
 * wrapper, so its clientHeight is the wrapper's VISIBLE height — correct as a
 * ceiling until a soft keyboard opens. The keyboard does not shrink layout
 * height, only the visual viewport, so without this subtraction a panel sizes
 * itself into the region the keyboard is covering and its focused field ends up
 * underneath. `getKeyboardInset()` is 0 on desktop and whenever no keyboard is
 * up, so this is a no-op everywhere else.
 */
function _usableHeight(px) {
  if (!Number.isFinite(px)) return px;
  return Math.max(PANEL_MIN_BODY_HEIGHT_PX, px - getKeyboardInset());
}
const PANEL_MIN_BODY_HEIGHT_PX = 80;

function _pruneElementOverrideBuckets(overrides, html, removedEid = '') {
  const next = structuredClone(overrides || {});
  const pruneBucket = (bucket) => {
    if (!bucket?.elements) return;
    const elements = { ...bucket.elements };
    if (removedEid) delete elements[removedEid];
    const pruned = pruneOrphanOverrides(html, elements);
    if (pruned) bucket.elements = pruned;
    else delete bucket.elements;
  };

  pruneBucket(next);
  for (const viewport of RESPONSIVE_VIEWPORTS) {
    const bucket = next.responsive?.[viewport];
    if (!bucket) continue;
    pruneBucket(bucket);
    if (!Object.keys(bucket).length) delete next.responsive[viewport];
  }
  if (next.responsive && !Object.keys(next.responsive).length) delete next.responsive;
  return Object.keys(next).length ? next : null;
}

function _elementOverrideSummary(section, eid) {
  const fields = new Set();
  const collect = (properties) => {
    for (const [field, value] of Object.entries(properties || {})) {
      if (field === '__important' || value == null || value === '') continue;
      fields.add(field);
    }
  };
  collect(section?.overrides?.elements?.[eid]);
  for (const viewport of RESPONSIVE_VIEWPORTS) {
    collect(section?.overrides?.responsive?.[viewport]?.elements?.[eid]);
  }
  return { count: fields.size, fields: Array.from(fields) };
}

function _panelRectSignature(panel, rect) {
  if (!panel || !rect) return null;
  const instanceId = panel._data?.instanceId || '';
  const eid = panel._data?.eid || '';
  if (!instanceId && !eid) return null;
  const id = `${instanceId}|${eid}`;
  const top = Number.isFinite(rect.top) ? rect.top.toFixed(2) : '';
  const left = Number.isFinite(rect.left) ? rect.left.toFixed(2) : '';
  const width = Number.isFinite(rect.width) ? rect.width.toFixed(2) : '';
  const height = Number.isFinite(rect.height) ? rect.height.toFixed(2) : '';
  return `${id}|${top}|${left}|${width}|${height}`;
}

async function _showToast(message, type = 'info') {
  if (!_showToastFn) {
    try {
      const mod = await import('../ui/shell.js');
      _showToastFn = mod.showToast;
    } catch {
      _showToastFn = null;
    }
  }
  if (_showToastFn) _showToastFn(message, type);
}

async function _showConfirmModal(options) {
  if (!_showConfirmModalFn) {
    try {
      const mod = await import('../ui/shell.js');
      _showConfirmModalFn = mod.showConfirmModal;
    } catch {
      _showConfirmModalFn = null;
    }
  }
  if (_showConfirmModalFn) return _showConfirmModalFn(options);
  return false;
}

async function _showUndoToast(message) {
  if (!_showUndoToastFn) {
    try {
      const mod = await import('../ui/shell.js');
      _showUndoToastFn = mod.showUndoToast;
    } catch {
      _showUndoToastFn = null;
    }
  }
  if (_showUndoToastFn) _showUndoToastFn(message);
}

export function initGlassPanels(overlay) {
  if (_overlay) {
    destroyGlassPanels();
  }
  _overlay = overlay;
  _wireEvents();
}

export function destroyGlassPanels() {
  _cancelPendingOpen();
  dismissAll();
  for (const [event, fn] of _listeners) {
    eventBus.off(event, fn);
  }
  _listeners = [];
  if (_docListenersWired) {
    document.removeEventListener('mousedown', _onDocumentClick, true);
    document.removeEventListener('keydown', _onKeyDown);
    _docListenersWired = false;
  }
  _offViewport?.();
  _offViewport = null;
  _overlay = null;
}

function _listen(event, fn) {
  eventBus.on(event, fn);
  _listeners.push([event, fn]);
}

function _wireEvents() {
  _listen('iframe:element-clicked', _onElementClicked);
  _listen('iframe:section-clicked', _onSectionClicked);
  _listen('glass:toggle-section-panel', _onToggleSectionPanel);
  _listen('glass:island-positioned', _onIslandPositioned);
  _listen('iframe:element-rect', _onElementRect);
  _listen('iframe:element-rect-update', _onElementRectUpdate);
  _listen('iframe:selected-editable-cleared', _onSelectedEditableCleared);
  _listen('section-deselected', _onDeselected);
  _listen('editable-ids:repaired', _onEditableIdsRepaired);

  // Click-outside dismiss
  if (!_docListenersWired) {
    // pointerdown, not the mousedown+touchstart pair: that pair double-fires
    // on hybrid devices, which report both. shell.js made the same choice.
    document.addEventListener('pointerdown', _onDocumentClick, true);

    // Escape dismiss
    document.addEventListener('keydown', _onKeyDown);
    _docListenersWired = true;
  }

  // Rotation, breakpoint crossings and the soft keyboard all change how much
  // room a floating panel has, and none of them produce an iframe rect event.
  _offViewport?.();
  _offViewport = onViewportChange(_reflowOpenPanels);
}

/**
 * Open the registered panel for an editable — the PUBLIC constructor.
 *
 * SEL-2 keeps this constructor separate from the request service: canvas
 * clicks, row 2 and the left rail all request through one policy/rect matcher,
 * and only a settled, clear placement reaches this function.
 *
 * ── The element-claim arbitration is preserved ────────────────────────────
 * `glass:element-claimed` / `isElementClaimed()` is how other listeners know a
 * click was consumed by a panel. Emitting it from HERE rather than from the
 * click handler is deliberate: the claim belongs to "a panel opened for this
 * editable", not to "a click happened". Callers that are not a click pass
 * `claim: false` so they do not fabricate one.
 *
 * @param {object} payload — a full `iframe:element-clicked` payload. The panels
 *   read far more than {instanceId, eid, editableType} off it (computed styles,
 *   href/target, per-type extras), so a caller holding only an identity must
 *   resolve the payload first — `editing/active-editable-store.js` keeps it.
 * @param {{triggerEl?: HTMLElement|null, claim?: boolean, rect?: object|null, allowOverlap?: boolean}} [opts]
 *   `triggerEl` is the focus-restore target when the panel closes. `allowOverlap` (an explicit
 *   open only) lets the panel cover its element when no clear room is visible.

 * @returns {GlassPanel|null} the opened panel, or null when the type has none.
 */
export function openPanelFor(payload, opts = {}) {
  if (!_overlay || !payload) return null;

  const PanelClass = _registry.get(panelKeyFor(payload));
  if (!PanelClass) return null;

  const { triggerEl = null, claim = true, rect: settledRect = null, allowOverlap = false } = opts;

  // Dismiss existing panels FIRST.
  //
  // The claim is set AFTER this, and the order is load-bearing: `dismissAll`
  // clears `_claimedClick`, so the original sequence (claim, emit, dismiss)
  // erased the claim it had just made. `isElementClaimed()` therefore answered
  // `false` for the panel that was open at that very moment — the protocol
  // documented itself as working while doing the opposite.
  //
  // It went unnoticed because the protocol has no consumers: `isElementClaimed`
  // is exported and imported by nobody, and `glass:element-claimed` has no
  // listeners anywhere in the tree. Found by CMD-6's first test of the claim,
  // and fixed rather than documented, because a stated contract that is false is
  // worse than one that is absent.
  dismissAll({ clearSelection: false });
  if (settledRect) _lastElementRect = settledRect;

  if (claim) {
    // Claim this open — other listeners check isElementClaimed()
    _claimedClick = { instanceId: payload.instanceId, eid: payload.eid };
    eventBus.emit('glass:element-claimed', _claimedClick);
  }

  // Create and show the panel
  const panel = new PanelClass(_overlay, payload);
  _panelConstructionCount += 1;
  // Focus restore target. `dismissAll` destroys panels without consulting it,
  // so a panel opened from a toolbar button records where focus came from and
  // its own teardown returns it — the same contract `openPopoverMenu` holds.
  if (triggerEl) panel._pbRestoreFocusTo = triggerEl;
  panel._allowOverlap = allowOverlap;
  _activePanels.push(panel);

  const content = _overlay.getContentElement();
  if (content) {
    content.appendChild(panel.element);
    // Position once we have a rect (may arrive same tick or next)
    if (
      _lastElementRect &&
      _lastElementRect.instanceId === payload.instanceId &&
      targetRefKey(_lastElementRect.targetRef) === targetRefKey(payload.targetRef)
    ) {
      panel.positionNear(_lastElementRect.rect);
      panel._lastPositionedSignature = _panelRectSignature(panel, _lastElementRect.rect);
    }
  }
  return panel;
}

/** SEL-2: a canvas click requests a panel; construction waits for its rect. */
function _onElementClicked(data) {
  if (!data) return;
  _claimedClick = null;
  requestPanelOpen(data, { source: 'canvas-click' });
}

/** Queue one panel open and settle it only against a matching rect. */
export function requestPanelOpen(payload, opts = {}) {
  if (!_overlay || !payload) return { open: false, reason: 'no-panel' };
  const policy = shouldAutoOpenPanel(payload, {
    hasPanel: hasPanelFor(payload),
    killSwitch: opts.explicit ? true : glassAutoOpenEnabled(),
    phone: opts.explicit ? false : isPhoneViewport(),
    pickMode: opts.explicit ? false : isPickModeActive(),
    elementMulti: opts.explicit ? false : elementSelectionSize() > 1,
    textEditIntent: opts.explicit ? false : !!payload.editIntent,
    textEditLive: false
  });
  if (!policy.open) {
    _cancelPendingOpen();
    return policy;
  }
  const key = `${payload.instanceId}|${targetRefKey(payload.targetRef)}|${payload.editableType || ''}|${payload.kind || ''}`;
  const openPanel = _activePanels.find((panel) => {
    const held = panel?._data;
    return (
      held &&
      `${held.instanceId}|${targetRefKey(held.targetRef)}|${held.editableType || ''}|${held.kind || ''}` ===
        key
    );
  });
  if (openPanel || _pendingOpen?.key === key) {
    // `toggle` — a second press of the control that opened it closes it again.
    //
    // Only the EXPLICIT routes ask for this. Auto-open must never close a
    // panel: it fires from selection and rect settlement, so a toggling
    // auto-open would shut the panel the moment anything re-reported the
    // element the user is working in.
    //
    // A matching PENDING open counts as open. The request is asynchronous — it
    // waits on `query-element-rect` — so the second press can land in the ~250ms
    // before the panel paints, and treating that as "nothing to toggle" would
    // open a panel the user has just asked to be rid of.
    if (opts.toggle) {
      _cancelPendingOpen();
      if (openPanel) dismissPanel(openPanel);
      return { open: false, reason: 'toggled-closed', toggled: true };
    }
    return { open: true, reason: null, deduped: true };
  }

  _cancelPendingOpen();
  const token = ++_openToken;
  _pendingOpen = {
    token,
    key,
    payload,
    triggerEl: opts.triggerEl || null,
    focusField: opts.focusField || null,
    claim: opts.claim !== false,
    // Edit… / All settings: a request, which may cover its element when nothing else is
    // visible (SYS-8 F-6). An auto-open never may.
    explicit: !!opts.explicit,
    timeoutId: setTimeout(() => {
      if (_pendingOpen?.token === token) _cancelPendingOpen();
    }, 250)
  };
  // The iframe reports the currently selected target. CSS backgrounds have no
  // eid, but an explicit row-2/rail open still needs a fresh rect after a prior
  // panel dismissal cleared `_lastElementRect`.
  bridge.send('query-element-rect', {
    instanceId: payload.instanceId,
    targetRef: payload.targetRef,
    ...(isDomTarget(payload) ? { eid: payload.eid } : {})
  });
  return { open: true, reason: null, pending: true };
}

function _onSectionClicked(data) {
  if (!_overlay || !data) return;
  // Section click only selects — dismiss any open panel.
  // Settings panel is opened explicitly via the island settings button.
  _claimedClick = null;
  _cancelPendingOpen();
  dismissAll();
}

function _onSelectedEditableCleared() {
  _claimedClick = null;
  _lastElementRect = null;
  _cancelPendingOpen();
  const hasElementPanel = _activePanels.some((panel) => panel?._data?.editableType);
  if (hasElementPanel) dismissAll();
}

async function _onToggleSectionPanel(data) {
  if (!_overlay || !data?.instanceId) return;

  // If a section panel is already open for this instanceId, close it (toggle)
  if (_activePanels.length > 0 && _activePanels[0]._data?.instanceId === data.instanceId) {
    dismissAll();
    return;
  }

  dismissAll();

  const PanelClass = await _getSectionPanel();
  const panel = new PanelClass(_overlay, data);
  _activePanels.push(panel);

  const content = _overlay.getContentElement();
  const overlayEl = _overlay.getOverlayElement();
  if (!content) return;

  // Sticky-island path: anchor panel to the island element so it remains
  // directly beneath the toolbar while iframe content scrolls.
  if (overlayEl && (data.anchorEl || data.anchorRect)) {
    overlayEl.appendChild(panel.element);
    panel._sectionAnchorEl = data.anchorEl || null;
    panel._sectionAnchorRect = data.anchorRect || null;
    _positionSectionPanelBelowAnchor(panel, overlayEl);
    return;
  }

  // Fallback: legacy section-relative placement.
  content.appendChild(panel.element);
  const sectionEl = _overlay.getSectionElement(data.instanceId);
  if (sectionEl) {
    const sectionTop = parseFloat(sectionEl.style.top) || 0;
    const sectionWidth = sectionEl.offsetWidth || 800;
    const panelWidth = 280;
    const gap = 8;

    const top = sectionTop + 8 + 38 + gap;
    const left = sectionWidth - panelWidth - 8;

    panel.element.style.position = 'absolute';
    panel.element.style.top = `${top}px`;
    panel.element.style.left = `${left}px`;
    panel.element.style.width = `${panelWidth}px`;
    panel.element.style.maxWidth = `${panelWidth}px`;
  }
}

function _sectionAnchorRect(panel) {
  const anchorEl = panel?._sectionAnchorEl;
  if (anchorEl?.isConnected && typeof anchorEl.getBoundingClientRect === 'function') {
    const rect = anchorEl.getBoundingClientRect();
    panel._sectionAnchorRect = rect;
    return rect;
  }
  return panel?._sectionAnchorRect || null;
}

function _positionSectionPanelBelowAnchor(panel, overlayEl) {
  if (panel?._positionPhoneSheet?.()) return;

  const anchorRect = _sectionAnchorRect(panel);
  if (!panel?.element || !overlayEl || !anchorRect) return;

  const panelEl = panel.element;
  const overlayRect = overlayEl.getBoundingClientRect();
  const gap = 8;
  const inset = PANEL_INSET_PX;
  const visibleBounds = _visibleBounds(overlayEl);
  const panelWidth = Math.min(
    panelWidthPx(),
    (visibleBounds ? visibleBounds.width : overlayEl.clientWidth) - inset * 2
  );

  const anchorTop = anchorRect.top - overlayRect.top;
  const anchorLeft = anchorRect.left - overlayRect.left;
  const anchorWidth = anchorRect.width || 0;
  const anchorHeight = anchorRect.height || 0;

  panelEl.style.position = 'absolute';
  panelEl.style.width = `${panelWidth}px`;
  panelEl.style.maxWidth = `${panelWidth}px`;

  let left = anchorLeft + anchorWidth - panelWidth;
  left = Math.min(
    Math.max(inset, left),
    Math.max(inset, overlayEl.clientWidth - panelWidth - inset)
  );
  if (visibleBounds) left = _clampLeftToBounds(left, panelWidth, visibleBounds, inset);

  const top = anchorTop + anchorHeight + gap;

  panelEl.style.left = `${left}px`;
  panelEl.style.top = `${top}px`;

  const bodyEl = panelEl.querySelector('.pb-glass-panel-body');
  if (bodyEl) {
    const headerH = panelEl.querySelector('.pb-glass-panel-header')?.offsetHeight || 36;
    const available = _usableHeight(overlayEl.clientHeight) - top - headerH - inset * 2;
    const maxBody = Math.max(PANEL_MIN_BODY_HEIGHT_PX, available);
    bodyEl.style.maxHeight = `${maxBody}px`;
  }
}

/**
 * Re-run the last positioning decision for every open panel.
 *
 * Panels are placed once and then tracked by iframe rect events. A viewport
 * change produces no rect event — nothing inside the iframe moved — so before
 * this existed, rotating the device or opening the keyboard left every open
 * panel at its stale size and position.
 */
function _reflowOpenPanels() {
  if (!_overlay) return;
  const overlayEl = _overlay.getOverlayElement();
  if (!overlayEl) return;
  for (const panel of _activePanels) {
    if (!panel?.element?.isConnected) continue;
    if (!panel._data?.eid) {
      _positionSectionPanelBelowAnchor(panel, overlayEl);
      continue;
    }
    if (_lastElementRect?.eid !== panel._data.eid) continue;
    // Clear the signature or _onElementRect's pure-scroll gate would also skip
    // the next genuine update, since the rect itself has not changed.
    panel._lastPositionedSignature = null;
    panel.positionNear(_lastElementRect.rect);
  }
}

function _onIslandPositioned(data) {
  if (!_overlay || !data?.instanceId) return;
  const overlayEl = _overlay.getOverlayElement();
  if (!overlayEl) return;

  for (const panel of _activePanels) {
    if (panel._data?.eid) continue;
    if (panel._data?.instanceId !== data.instanceId) continue;
    panel._sectionAnchorEl = data.anchorEl || panel._sectionAnchorEl || null;
    panel._sectionAnchorRect = data.anchorRect || panel._sectionAnchorRect || null;
    _positionSectionPanelBelowAnchor(panel, overlayEl);
  }
}

function _onElementRect(data) {
  if (!data) return;
  _lastElementRect = data;
  if (_pendingOpen) {
    const rectKey = `${data.instanceId}|${targetRefKey(data.targetRef)}|${data.editableType || ''}|${data.kind || ''}`;
    if (rectKey === _pendingOpen.key) {
      const pending = _pendingOpen;
      if (pending.timeoutId) clearTimeout(pending.timeoutId);
      _pendingOpen = null;
      const overlayEl = _overlay?.getOverlayElement?.();
      const content = _overlay?.getContentElement?.();
      const visible = _visibleBounds(overlayEl);
      const scrollY = _overlay?.getScrollY?.() || 0;
      const size = { width: panelWidthPx(), height: 360 };
      const bounds = {
        left: visible?.left || 0,
        top: scrollY,
        width: visible?.width || content?.offsetWidth || 800,
        height: _usableHeight(overlayEl?.clientHeight || window.innerHeight || 800)
      };
      const placement = placeClearOf(data.rect, size, bounds);
      const placed =
        placement.strategy === 'none' && pending.explicit
          ? placeVisible(data.rect, size, bounds)
          : placement;
      if (placed.strategy !== 'none') {
        const panel = openPanelFor(pending.payload, {
          triggerEl: pending.triggerEl,
          claim: pending.claim,
          rect: data,
          allowOverlap: pending.explicit
        });
        if (panel && pending.focusField) {
          requestAnimationFrame(() => {
            panel.element?.querySelector?.(pending.focusField)?.focus?.();
          });
        }
      } else {
        eventBus.emit('glass:autoopen-refused', {
          reason: 'no-room',
          instanceId: pending.payload.instanceId || null,
          targetRef: pending.payload.targetRef || null
        });
      }
    }
  }
  for (const panel of _activePanels) {
    const panelInstanceId = panel._data?.instanceId || null;
    const panelRefKey = targetRefKey(panel._data?.targetRef);
    const dataRefKey = targetRefKey(data.targetRef);
    const isMatch =
      panelInstanceId === data.instanceId &&
      (panelRefKey
        ? panelRefKey === dataRefKey
        : (panel._data?.eid || null) === (data.eid || null));
    if (!isMatch) continue;

    const sig = _panelRectSignature(panel, data.rect);
    if (sig && sig === panel._lastPositionedSignature) {
      // Pure-scroll frames are already handled by the glass content transform.
      // Remove this gate if/when element rect updates gain an explicit changeKind.
      continue;
    }

    panel.positionNear(data.rect);
    panel._lastPositionedSignature = sig;
  }
}

function _onElementRectUpdate(data) {
  // Same as element-rect but for continuous tracking
  _onElementRect(data);
}

function _onEditableIdsRepaired({ instanceIds = [] } = {}) {
  if (instanceIds.includes(_pendingOpen?.payload.instanceId)) _cancelPendingOpen();
  if (instanceIds.includes(_claimedClick?.instanceId)) _claimedClick = null;
  if (instanceIds.includes(_lastElementRect?.instanceId)) _lastElementRect = null;
  for (let index = _activePanels.length - 1; index >= 0; index--) {
    const panel = _activePanels[index];
    if (!instanceIds.includes(panel._data?.instanceId)) continue;
    if (typeof panel.retireEditableIdentity === 'function') panel.retireEditableIdentity();
    else panel.destroy();
    _activePanels.splice(index, 1);
  }
}

function _onDeselected() {
  _claimedClick = null;
  _cancelPendingOpen();
  dismissAll();
}

function _onDocumentClick(e) {
  if (_activePanels.length === 0) return;
  // Don't dismiss if clicking inside a panel
  for (const panel of _activePanels) {
    if (panel.element.contains(e.target)) return;
  }
  // Don't dismiss if clicking on the glass overlay controls.
  if (
    e.target.closest(
      '.pb-glass-island, .pb-glass-space-handle, .pb-glass-el-space, .pb-glass-add-btn, .pb-glass-resize-handle, .pb-glass-logo-handle'
    )
  )
    return;
  // Panel visibility is not selection identity. Row 2 and the rail are valid
  // destinations for the selection that opened this panel; clearing it during
  // pointerdown removes those controls before their click can run.
  dismissAll({ clearSelection: false });
}

function _onKeyDown(e) {
  if (e.key === 'Escape' && _activePanels.length > 0) {
    e.preventDefault();
    dismissAll();
  }
}

/**
 * Close ONE panel, leaving any other open and leaving the selection alone.
 *
 * `destroy()` is what returns focus to the control that opened it
 * (`_pbRestoreFocusTo`), so a toggled-shut panel hands the caret back to the
 * button that was just pressed rather than to the document.
 */
function dismissPanel(panel) {
  const index = _activePanels.indexOf(panel);
  if (index === -1) return false;
  _activePanels.splice(index, 1);
  panel.destroy();
  // `_claimedClick` names the click a panel took ownership of. Leaving it set
  // after that panel is gone would make `isElementClaimed` answer for a panel
  // that no longer exists.
  if (_claimedClick && _activePanels.length === 0) _claimedClick = null;
  return true;
}

function dismissAll({ clearSelection = false } = {}) {
  _cancelPendingOpen();
  const hadPanels = _activePanels.length > 0;
  for (const panel of _activePanels) {
    panel.destroy();
  }
  _activePanels.length = 0;
  _claimedClick = null;
  _lastElementRect = null;
  if (hadPanels && clearSelection) {
    // Closing a text panel is also an edit boundary. The iframe no-ops this for
    // non-text selections and emits editing-complete for an active text edit.
    bridge.send('disable-editing', {});
    bridge.send('clear-selected-editable', {});
    // Clear element-level overlay controls immediately (resize grabbers, spacing badges)
    // when panel actions dismiss selection without a canvas click.
    eventBus.emit('iframe:selected-editable-cleared');
  }
}

// ── Base Panel Class ──

export class GlassPanel {
  constructor(overlay, data) {
    this._overlay = overlay;
    this._data = data;
    this._el = document.createElement('div');
    this._el.className = 'pb-glass-panel';
    this._el.style.pointerEvents = 'auto';

    // Prevent clicks inside panel from bubbling to overlay dismiss
    this._el.addEventListener('mousedown', (e) => e.stopPropagation());

    // Build header
    const header = document.createElement('div');
    header.className = 'pb-glass-panel-header';

    const isElementPanel = isDomTarget(data);
    const headerExtras = this.renderHeaderExtras() || '';
    if (isElementPanel) {
      const resolved = resolveSectionByInstance(data.instanceId);
      const isTemplate = !!resolved?.section?.catalogKey;
      header.innerHTML = `
        <span class="pb-glass-panel-title">${this.title || 'Properties'}</span>
        <div class="pb-glass-panel-actions">
          ${headerExtras}
          <button class="pb-glass-panel-action pb-glass-panel-reset" aria-label="Reset to original" data-tooltip="Reset to original"${isTemplate ? '' : ' style="display:none"'}>
            ${icon('reset')}
          </button>
          <button class="pb-glass-panel-action pb-glass-panel-delete" aria-label="Delete element" data-tooltip="Delete element"${this.showDeleteAction() ? '' : ' hidden'}>
            ${icon('trash')}
          </button>
          <button class="pb-glass-panel-close" aria-label="Close panel">&times;</button>
        </div>
      `;
      header.querySelector('.pb-glass-panel-reset')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this._resetElement(data).catch((err) => {
          reportError('Element reset', err, { user: 'toast' });
        });
      });
      header
        .querySelector('.pb-glass-panel-delete')
        ?.addEventListener('click', () => this._deleteElement(data));
    } else {
      header.innerHTML = `
        <span class="pb-glass-panel-title">${this.title || 'Properties'}</span>
        <div class="pb-glass-panel-actions">
          ${headerExtras}
          <button class="pb-glass-panel-close" aria-label="Close panel">&times;</button>
        </div>
      `;
    }

    header.querySelector('.pb-glass-panel-close').addEventListener('click', () => {
      dismissAll();
    });
    this._headerEl = header;
    this._el.appendChild(header);

    // Build body
    const body = document.createElement('div');
    body.className = 'pb-glass-panel-body';
    this._bodyEl = body;
    this._el.appendChild(body);
    if (isPhoneViewport()) this._enablePhoneSheet();

    // Subclass renders content. If the subclass defines _renderHeaderChips()
    // and returns descriptors, mount the chip strip after renderContent so
    // the chip click handlers can target the now-existing tabpanels/fields.
    this.renderContent(body, data);
    this._mountHeaderChips();
    if (data.eid) {
      this._sharedSpacingControls = this._renderSharedElementControls(body, data);
      // EA-9: the per-element entrance row — ONE base-class mount covers every
      // eid-bearing panel (the plan's five and any future one), the same seam
      // the spacing widget and the reset row already ride.
      this._elementEntranceRow = mountElementEntranceRow({ container: body, data });
      this._mountElementOverrideReset(body, data);
    }
    this._initRefreshLifecycle(data);
  }

  /**
   * Keep an open panel live after external state changes. Subscribes to
   * state-changed / history-changed / theme:override-changed (re-derive from
   * persisted state via _refreshFromState) and iframe:style-snapshot (computed
   * readback via _syncFromSnapshot). For element panels, requests an initial
   * snapshot. All refreshes are read-only (no store writes) so reacting to the
   * panel's own write can't loop; refreshes are rAF-debounced. Torn down in
   * destroy(). Subclasses implement _refreshFromState / _syncFromSnapshot
   * (Phase 5); the base methods are no-ops so this is dormant until then.
   */
  _initRefreshLifecycle(data) {
    this._refreshUnsubs = [];
    const onExternal = () => this._scheduleRefresh();
    const onOverrideChanged = (payload) => {
      if (this._shouldRefreshForOverride(payload)) this._scheduleRefresh();
    };
    this._refreshUnsubs.push(eventBus.on('state-changed', onExternal));
    this._refreshUnsubs.push(eventBus.on('history-changed', onExternal));
    this._refreshUnsubs.push(eventBus.on('theme:override-changed', onOverrideChanged));
    this._refreshUnsubs.push(eventBus.on('viewport:changed', onExternal));
    this._refreshUnsubs.push(eventBus.on('responsive-edit-scope:changed', onExternal));
    this._refreshUnsubs.push(eventBus.on('iframe:style-invalidated', onExternal));
    // Phase 6 apply-and-verify outcome: a failed verify (the override lost the
    // cascade even after !important escalation) marks the panel so the header
    // shows a "Style not applied" badge — never a lying active swatch. Any
    // later successful verify for the same target clears it.
    this._refreshUnsubs.push(
      eventBus.on('style-verify:result', (res) => {
        if (!res || !this._el || !data) return;
        if (res.instanceId !== data.instanceId || (res.eid || '') !== (data.eid || '')) return;
        this._el.classList.toggle('pb-gp-verify-failed', res.applied === false);
      })
    );
    if (data && isDomTarget(data)) {
      this._refreshUnsubs.push(
        eventBus.on('iframe:style-snapshot', (snap) => {
          if (!snap || snap.instanceId !== data.instanceId || snap.eid !== data.eid) return;
          this._snapshot = snap;
          try {
            this._syncFromSnapshot(snap);
          } catch (err) {
            console.warn('[GlassPanel] snapshot sync failed:', err);
          }
        })
      );
      this.requestSnapshot('open');
    }
  }

  showDeleteAction() {
    return isDomTarget(this._data) && this._data?.kind !== 'decoration';
  }

  _shouldRefreshForOverride(payload) {
    if (!payload || typeof payload !== 'object') return true;
    const data = this._data || {};
    const scope = payload.scope || '';
    if (scope === 'element') {
      if (!payload.instanceId || !payload.eid) return true;
      return payload.instanceId === data.instanceId && payload.eid === data.eid;
    }
    if (scope === 'section') {
      if (!payload.instanceId) return true;
      return payload.instanceId === data.instanceId;
    }
    return true;
  }

  _scheduleRefresh() {
    if (this._refreshQueued) return;
    this._refreshQueued = true;
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb) => setTimeout(cb, 16);
    raf(() => {
      this._refreshQueued = false;
      if (!this._el) return; // destroyed between schedule and fire
      this.refresh();
    });
  }

  /** Request a fresh StyleSnapshot for this element (no-op for section panels). */
  requestSnapshot(reason = 'refresh') {
    const d = this._data;
    if (!d || !d.eid) return;
    _snapshotRequestCount += 1;
    bridge.requestStyleSnapshot({ instanceId: d.instanceId, eid: d.eid, reason });
  }

  /** Re-sync from an external state change: re-read persisted state, then
   *  request a fresh computed snapshot. Read-only. */
  refresh(reason = 'refresh') {
    try {
      this._refreshFromState();
    } catch (err) {
      console.warn('[GlassPanel] refresh failed:', err);
    }
    refreshElementEntranceRow(this._elementEntranceRow);
    this._refreshElementOverrideReset();
    this.requestSnapshot(reason);
  }

  /** Override in subclass: re-derive control values from persisted overrides. */
  _refreshFromState() {}

  /** Override in subclass: reflect the element's computed readback + token identity. */
  _syncFromSnapshot(snapshot) {}

  get element() {
    return this._el;
  }
  get title() {
    return 'Properties';
  }

  /** Override in subclass to render panel content. */
  renderContent(container, data) {}

  /** Shared spacing controls appended to every element panel. When the panel
   *  uses tabs (renderContent emitted `[role="tabpanel"][data-tab="spacing"]`),
   *  the spacing controls land inside the Spacing tabpanel; otherwise they
   *  append to the body root as before. */
  _renderSharedElementControls(container, data) {
    const spacingPanel = container.querySelector('[data-tab="spacing"]');
    const target = spacingPanel || container;
    return mountSharedSpacingControls({ panel: this, container: target, data });
  }

  _mountElementOverrideReset(container, data) {
    const row = document.createElement('div');
    row.className = 'pb-gp-element-reset-row';
    row.innerHTML = `
      <div class="pb-gp-element-reset-copy">
        <span class="pb-gp-element-reset-title">Element tweaks</span>
        <span class="pb-gp-element-reset-meta" aria-live="polite"></span>
      </div>
      <button type="button" class="pb-gp-inline-link pb-gp-element-reset-btn">Reset</button>
    `;
    row.querySelector('.pb-gp-element-reset-btn').addEventListener('click', () => {
      this._resetElementOverrides(data);
    });
    container.appendChild(row);
    this._elementOverrideResetEl = row;
    this._refreshElementOverrideReset();
  }

  _refreshElementOverrideReset() {
    const row = this._elementOverrideResetEl;
    const data = this._data;
    if (!row || !data?.eid) return;
    const section = resolveSectionByInstance(data.instanceId)?.section;
    const summary = _elementOverrideSummary(section, data.eid);
    const meta = row.querySelector('.pb-gp-element-reset-meta');
    const button = row.querySelector('.pb-gp-element-reset-btn');
    if (meta) {
      meta.textContent = summary.count
        ? `${summary.count} modified ${summary.count === 1 ? 'property' : 'properties'}`
        : 'Using template defaults';
    }
    if (button) button.disabled = summary.count === 0;
    row.classList.toggle('pb-gp-element-reset-row--active', summary.count > 0);
  }

  _resetElementOverrides(data) {
    const section = resolveSectionByInstance(data.instanceId)?.section;
    const summary = _elementOverrideSummary(section, data.eid);
    if (!summary.count) return;
    const changes = Object.fromEntries(summary.fields.map((field) => [field, null]));
    const changed = applyElementOverrides(data.instanceId, data.eid, changes, {
      source: 'glass-element-reset',
      label: 'Reset element tweaks'
    });
    if (!changed) return;
    bridge.send('query-element-rect', { instanceId: data.instanceId, eid: data.eid });
    this._scheduleRefresh();
  }

  /** Override in subclass to inject extra HTML into the header actions row (rendered before the action buttons). Must return a string. */
  renderHeaderExtras() {
    return '';
  }

  /** Position the panel near a rect (document-relative coordinates). */
  positionNear(rect, options = {}) {
    if (!rect || !this._el) return;
    if (this._positionPhoneSheet()) return;
    const isGroupAnchor = !!options.group;

    const panelGap = 12;
    const contentPadding = PANEL_INSET_PX;
    this._el.classList.remove('pb-glass-panel--spacing-mode');

    // Get the overlay/iframe width to determine available space
    const content = this._overlay.getContentElement();
    const contentWidth = content?.offsetWidth || 800;
    const visibleBounds = _visibleBounds(this._overlay.getOverlayElement());
    const panelWidth = visibleBounds
      ? Math.min(panelWidthPx(), visibleBounds.width - contentPadding * 2)
      : panelWidthPx();
    const contentHeight = content?.offsetHeight || window.innerHeight;
    const panelHeight = this._el.offsetHeight || 360;
    const overlayEl = this._overlay.getOverlayElement();
    const viewportHeight = _usableHeight(overlayEl?.clientHeight || contentHeight);
    const scrollY =
      typeof this._overlay.getScrollY === 'function' ? this._overlay.getScrollY() || 0 : 0;

    const fitsRight =
      rect.left + rect.width + panelGap + panelWidth <= contentWidth - contentPadding;
    const fitsBottom =
      rect.top + rect.height + panelGap + Math.min(panelHeight, 360) <=
      contentHeight - contentPadding;

    if (isGroupAnchor && fitsBottom) {
      let left = Math.min(
        Math.max(contentPadding, rect.left + rect.width / 2 - panelWidth / 2),
        Math.max(contentPadding, contentWidth - panelWidth - contentPadding)
      );
      if (visibleBounds) left = _clampLeftToBounds(left, panelWidth, visibleBounds, contentPadding);
      const top = Math.min(
        rect.top + rect.height + panelGap,
        Math.max(contentPadding, contentHeight - panelHeight - contentPadding)
      );
      this._el.style.position = 'absolute';
      this._el.style.left = `${left}px`;
      this._el.style.top = `${top}px`;
      this._el.style.width = `${panelWidth}px`;
      this._capBodyHeight(top, panelHeight, contentHeight, contentPadding, viewportHeight, scrollY);
      return;
    }

    if (isGroupAnchor && !fitsRight && !fitsBottom && options.allowMiniBar) {
      const miniAvailable = visibleBounds ? visibleBounds.width : contentWidth;
      const miniWidth = Math.min(520, Math.max(panelWidth, miniAvailable - contentPadding * 2));
      let left = Math.min(
        Math.max(contentPadding, rect.left + rect.width / 2 - miniWidth / 2),
        Math.max(contentPadding, contentWidth - miniWidth - contentPadding)
      );
      if (visibleBounds) left = _clampLeftToBounds(left, miniWidth, visibleBounds, contentPadding);
      this._el.classList.add('pb-glass-panel--spacing-mode');
      this._el.style.position = 'absolute';
      this._el.style.left = `${left}px`;
      this._el.style.top = `${scrollY + contentPadding + 8}px`;
      this._el.style.width = `${miniWidth}px`;
      const bodyEl = this._el.querySelector('.pb-glass-panel-body');
      if (bodyEl) bodyEl.style.maxHeight = '';
      return;
    }

    const size = { width: panelWidth, height: panelHeight };
    const bounds = {
      left: visibleBounds?.left || 0,
      top: scrollY,
      width: visibleBounds?.width || contentWidth,
      height: viewportHeight
    };
    const placeOpts = { panelGap, inset: contentPadding };
    let placement = placeClearOf(rect, size, bounds, placeOpts);
    // An explicit open (Edit…, All settings) covers its element rather than hide (SYS-8 F-6).
    if (placement.strategy === 'none' && this._allowOverlap) {
      placement = placeVisible(rect, size, bounds, placeOpts);
    }
    if (placement.strategy === 'none') {
      this._el.hidden = true;
      this._placementStrategy = 'none';
      eventBus.emit('glass:autoopen-refused', {
        reason: 'no-room',
        instanceId: this._data?.instanceId || null,
        targetRef: this._data?.targetRef || null
      });
      return placement;
    }
    this._el.hidden = false;
    this._placementStrategy = placement.strategy;

    this._el.style.position = 'absolute';
    this._el.style.left = `${placement.left}px`;
    this._el.style.top = `${placement.top}px`;
    this._el.style.width = `${placement.width}px`;
    this._capBodyHeight(
      placement.top,
      panelHeight,
      contentHeight,
      contentPadding,
      viewportHeight,
      scrollY
    );
    return placement;
  }

  _phoneSheetUsableHeight() {
    const overlayEl = this._overlay?.getOverlayElement?.();
    const h = overlayEl?.clientHeight || getViewportHeight() || window.innerHeight || 0;
    return _usableHeight(h);
  }

  _enablePhoneSheet() {
    if (this._phoneSheetEnabled || !this._el || !this._headerEl || !this._bodyEl) return;
    this._phoneSheetEnabled = true;
    this._phoneSheetDetent = 'half';
    this._el.classList.add('pb-sheet', 'pb-sheet--nonmodal', 'pb-glass-panel--phone-sheet');
    this._el.setAttribute('aria-modal', 'false');
    this._phoneSheetGrabber = createSheetGrabber();
    this._el.insertBefore(this._phoneSheetGrabber, this._el.firstChild);
    this._headerEl.classList.add('pb-sheet-header');
    this._bodyEl.classList.add('pb-sheet-body');
    this._applyPhoneSheetDetent('half');

    let heights = computeSheetDetents(this._phoneSheetUsableHeight());
    this._phoneSheetDrag = attachSheetDrag({
      card: this._el,
      grabber: this._phoneSheetGrabber,
      header: this._headerEl,
      body: this._bodyEl,
      detents: detentOffsets(heights),
      slackPx: 48,
      onDismiss: () => dismissAll(),
      onDragStart: () => {
        heights = computeSheetDetents(this._phoneSheetUsableHeight());
        this._el.style.setProperty('--pb-sheet-h', `${Math.round(heights.full)}px`);
        const index = GLASS_SHEET_DETENT_NAMES.indexOf(this._phoneSheetDetent);
        return detentOffsets(heights)[index >= 0 ? index : 1];
      },
      onSnap: (_offset, index) => {
        this._applyPhoneSheetDetent(GLASS_SHEET_DETENT_NAMES[index] || 'half', heights);
      }
    });
  }

  _applyPhoneSheetDetent(name, heights = computeSheetDetents(this._phoneSheetUsableHeight())) {
    if (!this._phoneSheetEnabled || !this._el) return;
    const detent = ['peek', 'half', 'full'].includes(name) ? name : 'half';
    const height = heights[detent] ?? heights.half;
    this._phoneSheetDetent = detent;
    this._el.style.setProperty('--pb-sheet-h', `${Math.round(height)}px`);
    this._el.style.transform = '';
    this._el.dataset.pbDetent = detent;
    this._positionPhoneSheet();
  }

  _positionPhoneSheet() {
    if (!this._phoneSheetEnabled || !this._el) return false;
    const overlayEl = this._overlay?.getOverlayElement?.();
    if (!overlayEl) return true;

    const visibleBounds = _visibleBounds(overlayEl);
    const left = visibleBounds?.left || 0;
    const width =
      visibleBounds?.width || overlayEl.clientWidth || window.innerWidth || panelWidthPx();
    const height = parseFloat(this._el.style.getPropertyValue('--pb-sheet-h')) || 0;
    const usableHeight = this._phoneSheetUsableHeight();
    const scrollY =
      typeof this._overlay?.getScrollY === 'function' ? this._overlay.getScrollY() || 0 : 0;
    const top = scrollY + Math.max(0, usableHeight - height);

    this._el.style.position = 'absolute';
    this._el.style.left = `${Math.round(left)}px`;
    this._el.style.right = 'auto';
    this._el.style.top = `${Math.round(top)}px`;
    this._el.style.bottom = 'auto';
    this._el.style.width = `${Math.max(0, Math.round(width))}px`;
    this._el.style.maxWidth = `${Math.max(0, Math.round(width))}px`;
    this._el.style.maxHeight = '';
    this._bodyEl.style.maxHeight = '';
    return true;
  }

  _capBodyHeight(
    top,
    panelHeight,
    contentHeight,
    contentPadding,
    viewportHeight = null,
    scrollY = 0
  ) {
    // Dynamically cap panel body so it never overflows the overlay
    const bodyEl = this._el.querySelector('.pb-glass-panel-body');
    if (bodyEl) {
      const headerH = this._el.querySelector('.pb-glass-panel-header')?.offsetHeight || 36;
      const visibleBottom = Number.isFinite(viewportHeight)
        ? scrollY + viewportHeight
        : contentHeight;
      const lowerBound = Math.min(contentHeight, visibleBottom);
      const available = lowerBound - top - headerH - contentPadding * 2;
      const maxBody = Math.max(PANEL_MIN_BODY_HEIGHT_PX, available);
      bodyEl.style.maxHeight = `${maxBody}px`;
    }
  }

  /** Delete the selected element from the section HTML. */
  async _deleteElement(data) {
    const resolved = resolveSectionByInstance(data.instanceId);
    if (!resolved) return;

    const section = resolved.section;
    const eid = data.eid;

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${section.html}</div>`, 'text/html');
    const el = doc.body.firstElementChild.querySelector(`[data-pb-eid="${eid}"]`);
    if (!el) return;
    const hasEditableChildren = el.querySelector('[data-pb-editable]') !== null;
    if (hasEditableChildren) {
      const isMac = navigator.platform?.startsWith('Mac');
      const confirmed = await _showConfirmModal({
        title: 'Delete Element',
        message: 'This element and its contents will be removed.',
        details: `You can undo with ${isMac ? '⌘' : 'Ctrl+'}Z.`,
        confirmLabel: 'Delete',
        danger: true
      });
      if (!confirmed || !this._el) return;
    }

    const updatedHtml = deleteEditableFromHTML(section.html, eid);
    if (updatedHtml === null) return;

    // Build pruned overrides across base and responsive element buckets.
    const nextOverrides = _pruneElementOverrideBuckets(section.overrides, updatedHtml, eid);
    const overridesChanged =
      JSON.stringify(section.overrides ?? null) !== JSON.stringify(nextOverrides);

    store.beginTransaction('Delete element');
    store.update(resolved.path, updatedHtml);
    if (overridesChanged) store.update(resolved.overridesPath, nextOverrides);
    store.commitTransaction();

    dismissAll({ clearSelection: true });
    await _showUndoToast('Element deleted');
    eventBus.emit('preview:sync-section-html', {
      instanceId: data.instanceId,
      syncOverrides: overridesChanged
    });
  }

  /** Reset the selected element to its original template markup. */
  async _resetElement(data) {
    const resolved = resolveSectionByInstance(data.instanceId);
    if (!resolved) return;

    const section = resolved.section;
    const eid = data.eid;
    if (!section.catalogKey || !section.category) return;

    // Use the persisted original snapshot. For legacy projects that predate
    // originalHtml, fall back to a stamped snapshot of current HTML rather than
    // fetching live catalog markup (which may have drifted and mismatch eids).
    let originalHtml = section.originalHtml;
    if (!originalHtml) {
      const { html: stampedCurrent } = stampEditableIds(section.html || '');
      originalHtml = stampedCurrent;
    }

    // Parse current first to validate we still have a target before reset.
    const parser = new DOMParser();
    const curDoc = parser.parseFromString(`<div>${section.html}</div>`, 'text/html');
    const curEl = curDoc.body.firstElementChild.querySelector(`[data-pb-eid="${eid}"]`);
    if (!curEl) return;

    const origDoc = parser.parseFromString(`<div>${originalHtml}</div>`, 'text/html');
    const origEl = origDoc.body.firstElementChild.querySelector(`[data-pb-eid="${eid}"]`);
    if (!origEl) {
      await _showToast('Element not found in original template', 'error');
      return;
    }

    const curType = curEl.getAttribute('data-pb-editable') || '';
    const origType = origEl.getAttribute('data-pb-editable') || '';
    // Guardrail: block cross-type replacements when legacy snapshots drift.
    if (curEl.tagName !== origEl.tagName || (curType && origType && curType !== origType)) {
      await _showToast('Reset blocked: original element no longer matches this element', 'error');
      return;
    }

    // Replace current element with original
    curEl.outerHTML = origEl.outerHTML;
    const updatedHtml = curDoc.body.firstElementChild.innerHTML;

    // Clear overrides for this eid across base and responsive buckets, then
    // prune any newly orphaned overrides.
    const nextOverrides = _pruneElementOverrideBuckets(section.overrides, updatedHtml, eid);

    store.beginTransaction('Reset element');
    store.update(resolved.path, updatedHtml);
    store.update(resolved.overridesPath, nextOverrides);
    store.commitTransaction();

    let previewResetHtml = origEl.outerHTML;
    try {
      const previewDoc = parser.parseFromString(`<div>${origEl.outerHTML}</div>`, 'text/html');
      const previewRoot = previewDoc.body.firstElementChild;
      if (previewRoot) {
        injectEditableAttrs(previewRoot);
        const previewEl = previewRoot.firstElementChild;
        if (previewEl) previewResetHtml = previewEl.outerHTML;
      }
    } catch {
      previewResetHtml = origEl.outerHTML;
    }

    // Apply the reset in-place to avoid full-canvas rerender flash/scroll jump.
    bridge.send('replace-editable-html', {
      instanceId: data.instanceId,
      eid,
      html: previewResetHtml
    });
    // Rebuild overrides CSS after clearing this element's override entry.
    eventBus.emit('theme:override-changed');
    bridge.send('query-element-rect', { instanceId: data.instanceId, eid });
    this._scheduleRefresh();
    await _showToast('Element restored to original', 'success');
  }

  // ─────────────────────────────────────────────────────────────
  // Tab + chip primitives (Pagy redesign)
  //
  // Subclasses opt in by:
  //  - Calling _renderTabs([...]) + _renderTabPanel(id, html) inside their
  //    renderContent template literal, then _wireTabs(container) after the
  //    innerHTML write.
  //  - Overriding _renderHeaderChips() to return chip descriptors.
  //
  // The base class persists this._activeTabId so re-renders don't bounce
  // the user back to the first tab.
  // ─────────────────────────────────────────────────────────────

  /** @returns {string} HTML for the tablist. Caller embeds via template literal. */
  _renderTabs(tabs) {
    const list = Array.isArray(tabs) ? tabs.filter((t) => t && t.id) : [];
    if (list.length === 0) return '';
    const activeId =
      this._activeTabId && list.some((t) => t.id === this._activeTabId)
        ? this._activeTabId
        : list[0].id;
    this._activeTabId = activeId;
    return `<div class="pb-gp-tabs" role="tablist">${list
      .map(
        (t) => `
      <button type="button" class="pb-gp-tab" role="tab"
        data-tab-id="${escapeAttr(t.id)}"
        aria-selected="${t.id === activeId ? 'true' : 'false'}"
        tabindex="${t.id === activeId ? '0' : '-1'}">${escapeHTML(t.label)}</button>`
      )
      .join('')}</div>`;
  }

  /** @returns {string} HTML for a single tabpanel wrapper. innerHTML embedded inline. */
  _renderTabPanel(id, innerHTML) {
    const active = this._activeTabId === id;
    return `<div class="pb-gp-tabpanel" role="tabpanel"
      data-tab="${escapeAttr(id)}"
      data-active="${active ? 'true' : 'false'}"${active ? '' : ' hidden'}>${innerHTML}</div>`;
  }

  /** Attach click + keyboard handlers to the tabs after innerHTML is written. */
  _wireTabs(container) {
    const tabs = Array.from(container.querySelectorAll('.pb-gp-tab'));
    if (tabs.length === 0) return;
    this._tabsRoot = container;
    for (const tab of tabs) {
      tab.addEventListener('click', () => this._setActiveTab(tab.dataset.tabId));
      tab.addEventListener('keydown', (e) => this._onTabKey(e, tabs));
    }
  }

  _onTabKey(event, tabs) {
    const idx = tabs.indexOf(event.currentTarget);
    if (idx < 0) return;
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown')
      next = tabs[(idx + 1) % tabs.length];
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
      next = tabs[(idx - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    event.preventDefault();
    this._setActiveTab(next.dataset.tabId);
    next.focus();
  }

  _setActiveTab(id) {
    if (!id) return;
    this._activeTabId = id;
    const root = this._tabsRoot || this._bodyEl;
    if (!root) return;
    for (const tab of root.querySelectorAll('.pb-gp-tab')) {
      const active = tab.dataset.tabId === id;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    }
    for (const panel of root.querySelectorAll('.pb-gp-tabpanel')) {
      const active = panel.dataset.tab === id;
      panel.hidden = !active;
      panel.dataset.active = active ? 'true' : 'false';
    }
  }

  /** Override in subclass — return [{ id, label, kind?, jumpToTab?, jumpToField?, title?, iconSvg? }, ...]. */
  _renderHeaderChips() {
    return [];
  }

  /** Build the chip strip and attach to the header. Idempotent. */
  _mountHeaderChips() {
    if (!this._headerEl) return;
    if (this._chipStripEl?.parentNode) this._chipStripEl.remove();
    this._chipStripEl = null;
    const descriptors = (this._renderHeaderChips() || []).filter(Boolean);
    if (descriptors.length === 0) return;
    const strip = document.createElement('div');
    strip.className = 'pb-gp-meta-chips';
    for (const d of descriptors) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `pb-gp-meta-chip${d.kind ? ' pb-gp-meta-chip-' + d.kind : ''}`;
      btn.dataset.chipId = d.id;
      if (d.title) btn.dataset.tooltip = d.title;
      if (d.iconSvg) {
        const iconWrap = document.createElement('span');
        iconWrap.className = 'pb-gp-meta-chip-icon';
        iconWrap.innerHTML = d.iconSvg;
        btn.appendChild(iconWrap);
      }
      const labelEl = document.createElement('span');
      labelEl.textContent = d.label;
      btn.appendChild(labelEl);
      if (d.jumpToTab) {
        btn.addEventListener('click', () => this._jumpToField(d.jumpToTab, d.jumpToField));
      } else {
        // BC-11: aria-disabled, not `disabled` — an informational chip keeps its
        // hover and focus so its tooltip (what the chip means) can show.
        btn.setAttribute('aria-disabled', 'true');
      }
      strip.appendChild(btn);
    }
    this._headerEl.appendChild(strip);
    this._chipStripEl = strip;
  }

  /** Re-derive descriptors and rebuild the chip strip from scratch. */
  _refreshHeaderChips() {
    this._mountHeaderChips();
  }

  /** Update a single chip's label (and optionally its tooltip) without
   *  rebuilding the strip. Used for per-keystroke updates (e.g. text panel
   *  char count, whose tooltip carries the live word count). */
  _setChipLabel(chipId, label, title) {
    const btn = this._chipStripEl?.querySelector(`[data-chip-id="${chipId}"]`);
    if (!btn) return;
    const span = btn.querySelector('span:last-child');
    if (span) span.textContent = label;
    if (typeof title === 'string') btn.dataset.tooltip = title;
  }

  /** Switch to a tab, focus a field inside it, pulse a focus ring. */
  _jumpToField(tabId, selector) {
    if (!tabId) return;
    this._setActiveTab(tabId);
    if (!selector || !this._bodyEl) return;
    requestAnimationFrame(() => {
      const target = this._bodyEl?.querySelector(selector);
      if (!target) return;
      const FOCUSABLE = 'input, select, textarea, button, [tabindex]';
      const focusable = target.matches(FOCUSABLE)
        ? target
        : target.querySelector('button[aria-pressed="true"], ' + FOCUSABLE);
      if (!focusable) return;
      try {
        focusable.focus();
      } catch {}
      focusable.scrollIntoView({ behavior: 'smooth', block: 'center' });
      focusable.style.transition = 'box-shadow 240ms ease';
      focusable.style.boxShadow = '0 0 0 3px rgba(15, 23, 42, 0.18)';
      setTimeout(() => {
        if (focusable.isConnected) focusable.style.boxShadow = '';
      }, 900);
    });
  }

  retireEditableIdentity() {
    this._identityRetired = true;
    this._sharedSpacingControls?.retireEditableIdentity();
    this.destroy();
  }

  destroy() {
    // Focus restore for panels opened from a control rather than from a canvas
    // click (`openPanelFor(payload, { triggerEl })`). Only restores when focus
    // is still INSIDE the panel — if the user has already moved on, stealing it
    // back is the bug, which is the same rule `openPopoverMenu` follows.
    const restoreTo = this._pbRestoreFocusTo;
    this._pbRestoreFocusTo = null;
    if (
      restoreTo &&
      this._el &&
      typeof restoreTo.focus === 'function' &&
      this._el.contains(document.activeElement) &&
      document.body.contains(restoreTo)
    ) {
      restoreTo.focus();
    }
    this._phoneSheetDrag?.destroy();
    this._phoneSheetDrag = null;
    if (this._refreshUnsubs) {
      for (const off of this._refreshUnsubs) {
        try {
          off();
        } catch {}
      }
      this._refreshUnsubs = null;
    }
    this._refreshQueued = false;
    this._snapshot = null;
    this._sharedSpacingControls?.destroy();
    this._sharedSpacingControls = null;
    if (this._el?.parentNode) {
      this._el.remove();
    }
    this._el = null;
    this._bodyEl = null;
    this._headerEl = null;
    this._chipStripEl = null;
    this._tabsRoot = null;
  }
}

// ── Small attribute escape helper — used by the base-class tab template ──
function escapeAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
